<script setup lang="ts">
import { getEmptySearchCriteria } from '@@/shared/property/search-criteria';
import type { AgentOnPage } from '@@/shared/agent/api-types';
import { SORT_OPTIONS, type PropertySearchCriteria, type SortOption } from '@@/shared/property/search-criteria';

const props = defineProps<{
  agent: AgentOnPage
  titleTag?: 'p' | 'h2' | 'h3'
}>();

const { t } = useI18n();

interface SelectOption { title: string, value: SortOption };
const sortOptions = computed<Array<SelectOption>>(() => {
  const SORT_OPTIONS_WITHOUT_PAID_AT = SORT_OPTIONS.filter(option => option !== 'paidAtDesc');
  return SORT_OPTIONS_WITHOUT_PAID_AT.map(option => ({
    title: t(`property_serp.sort.${option}`),
    value: option,
  }));
});
const sort = ref<SelectOption>({
  title: t(`property_serp.sort.dateDesc`),
  value: 'dateDesc',
});

const filterParams = ref<FilterParams>({
  propertySearchCriteria: getEmptySearchCriteria(),
  properties: props.agent.properties,
});

const { filtersCount, filteredProperties } = useAgentPropertyFilters(filterParams);
const displayAllProperties = ref(false);
const firstDisplayedProperties = computed(() => filteredProperties.value.slice(0, 15));
const hasMoreProperties = computed(() => filteredProperties.value.length > 15);
const otherProperties = computed(() => hasMoreProperties.value ? filteredProperties.value.slice(15, filteredProperties.value.length) : []);
const couldFilterProperties = computed(() => props.agent.properties.length >= 10);
const couldSortProperties = computed(() => props.agent.properties.length >= 3);

function onSort(selectedOption?: SelectOption) {
  if (!selectedOption) return;
  filterParams.value.propertySearchCriteria.sort = selectedOption.value;
  sort.value = { ...selectedOption };
}

const filtersDialogOpen = ref(false);

function updatePropertySearchCriteria(searchCriteria: PropertySearchCriteria) {
  filterParams.value.propertySearchCriteria = searchCriteria;
}
</script>

<template>
  <IadWrapper>
    <IadTitle
      :text="$t(`agent.properties.title`, { count: filteredProperties.length })"
      tag="h3"
      class="pb-xs"
    />

    <div class="flex gap-xs">
      <IadBtn
        v-if="couldFilterProperties"
        variant="outlined"
        @click="filtersDialogOpen = true"
      >
        <Icon name="iad-icon:tune" />
        {{ $t('core.common.filter', { count: filtersCount }) }}
      </IadBtn>
      <IadSelect
        v-if="couldSortProperties"
        class="min-w-40"
        name="sort"
        :model-value="sort"
        :items="sortOptions"
        @update:model-value="onSort"
      />
    </div>
    <section
      v-if="filteredProperties"
      class="grid mg:grid-cols-2 lg:grid-cols-3 gap-y-xl gap-x-m py-l"
    >
      <PropertyCard
        v-for="property of firstDisplayedProperties"
        :key="property.propertyListingRef"
        :property
        :title-tag
      />
      <div
        v-if="hasMoreProperties"
        :class="{
          hidden: !displayAllProperties,
          contents: displayAllProperties,
        }"
      >
        <PropertyCard
          v-for="property of otherProperties"
          :key="property.propertyListingRef"
          :property
          :title-tag
        />
      </div>
    </section>
    <div
      v-if="hasMoreProperties"
      class="flex justify-center"
    >
      <IadLinkMoreLess v-model="displayAllProperties" />
    </div>
    <ClientOnly>
      <AgentDialogPropertyFilter
        v-model="filtersDialogOpen"
        :property-search-criteria="filterParams.propertySearchCriteria"
        :properties="props.agent.properties"
        @filter="updatePropertySearchCriteria"
      />
    </ClientOnly>
  </IadWrapper>
</template>
